import type { ActionFunction, LinksFunction } from "@remix-run/node";
import {
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  json,
  isRouteErrorResponse,
  useRouteError,
  useLoaderData,
  useLocation,
  useNavigation,
  useParams,
  MetaFunction,
  redirect,
  Link,
  useSubmit,
} from "@remix-run/react";
import { cssBundleHref } from "@remix-run/css-bundle";
import {
  Button,
  Modal,
  ModalBody,
  ModalContent,
  HeroUIProvider,
  useDisclosure,
  ModalFooter,
} from "@heroui/react";
import toastifyCss from "react-toastify/dist/ReactToastify.css?url";

import { Flip, ToastContainer } from "react-toastify";
import { Header } from "app/components/Header";
import { Footer } from "app/components/Footer";
import { Sidebar } from "app/components/Sidebar";
import { ThemeProvider, useTheme } from "next-themes";

import "@fontsource-variable/mona-sans";

import "@fontsource/ubuntu/300.css";
import "@fontsource/ubuntu/400.css";
import "@fontsource/ubuntu/500.css";
import "@fontsource/ubuntu/700.css";

import "./tailwind.css";
import "./style.css";

// Import Swiper CSS directly from node_modules
import swiperBundleCss from "swiper/css/bundle?url";
import swiperFreeModeCss from "swiper/css/free-mode?url";

import { ReactNode, useEffect, useState } from "react";
import { destroySession, getSession } from "./auth/sessions";
import { ProgressBar } from "./nprogress";
import axios, { AxiosError } from "axios";

import { QueryClient, QueryClientProvider, useQueryClient } from "@tanstack/react-query";
import { loginAction } from "./actions/login.action";
import { findAllCategory } from "./service/category.server";
import { storageKey } from "./utils/storageKeys";
import ProductPageSkeleton from "./components/Product/ProductPageSkeleton";
import ChatWidget from "./components/ChatWidget";
import { fetchCategoryApi } from "./api/category/category.api";
import { categoryList } from "./atoms/category.atom";
import { useAtom, useSetAtom, useAtomValue } from "jotai";
import { userAtom } from "./atoms/user.atom";
import { VerifyUserToken } from "./api/auth.api";
import { RiUserUnfollowLine } from "react-icons/ri";
import { HiExclamationCircle, HiOutlineHome, HiOutlineRefresh } from "react-icons/hi";
import { MdOutlineErrorOutline, MdOutlineWifiOff } from "react-icons/md";
import { quotesList } from "./atoms/quote.atom";
import { cartList } from "./atoms/cart.atom";
import { logOutAction } from "./actions/logout.action";
import NeedsToLoginModal from "./components/Login/NeedsToLogin.modal";
import { PreviewProduct } from "./components/Product/PreviewProduct";
import { mergeCartApi } from "./api/cart/cart.api";
import { SortOrderSync } from "./components/SortOrderSync";

const queryClient = new QueryClient();

// Component to handle cart merge - must be inside QueryClientProvider
function CartMergeHandler() {
  const user = useAtomValue(userAtom);
  const [cart, setCartList] = useAtom(cartList);
  const queryClient = useQueryClient();

  // Merge guest cart with user cart on login
  useEffect(() => {
    // Only run if user just logged in and there are guest cart items (without userId)
    if (user && user._id && user.accessToken && cart.length > 0) {
      // Only merge if there are guest cart items (no userId)
      const guestCartItems = cart.filter((item) => !item.userId);
      if (guestCartItems.length === 0) return;
      const guestCartIds = guestCartItems
        .map((item) => (typeof item.sessionId === "string" ? item.sessionId : undefined))
        .filter((id): id is string => Boolean(id));
      if (guestCartIds.length === 0) return;

      mergeCartApi(
        { sessionIds: guestCartIds },
        {
          Authorization: `Bearer ${user.accessToken}`,
          "x-uid": user._id,
        },
      )
        .then((res) => {
          if (res.data) {
            setCartList(res.data); // Update local cart state with merged cart
            // Invalidate cart query to trigger refetch in Cart route and other components
            queryClient.invalidateQueries({ queryKey: ["userCart", user._id] });
          }
        })
        .catch((err) => {
          // Optionally handle error (e.g., show toast)
          console.error("Cart merge failed", err);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?._id]); // Only run when user logs in (user._id changes)

  return null; // This component doesn't render anything
}

interface RouteErrorResponse {
  status: number;
  statusText: string;
  data: any;
}

interface NetworkError extends Error {
  name: string;
}

// export function headers() {
//   return {
//     "cache-control": "max-age=120, stale-while-revalidate=86400",
//   };
// }

export const meta: MetaFunction = () => [
  { rel: "preconnect", href: "https://cdn.jsdelivr.net" },
  { rel: "preconnect", href: "https://www.googletagmanager.com" },
];

export const links: LinksFunction = () => [
  // Keep this empty: we attach non-blocking CSS link tags via JSX inside <head>
  // (Remix's LinksFunction doesn't support function-valued onLoad listeners).
];

let isMount = true;

export default function App() {
  return (
    <Document>
      <Outlet />
    </Document>
  );
}

export const action: ActionFunction = async ({ request }) => {
  const form = await request.formData();
  const islogoutAct = form.get("logout") === "true";

  if (islogoutAct) {
    return logOutAction(request);
  }

  return loginAction(request);
};

export async function loader({ request }: any) {
  const session = await getSession(request.headers.get("Cookie"));
  const uid = session.get("uid");
  const accessToken = session.get("accessToken");

  const categories = await findAllCategory();
  (globalThis as any).categories = categories;

  if (!uid || !accessToken) {
    return json({ user: null, categories: categories ? categories : [] });
  }
  try {
    const {
      data: { accessToken: _, ...user },
    } = await VerifyUserToken({ accessToken });
    return json({ user: { ...user, accessToken, uid }, categories: categories ? categories : [] });
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 401) {
      return redirect("/", {
        headers: { "Set-Cookie": await destroySession(session) },
      });
    }
    throw error;
  }
}

function Document(props: { children: ReactNode; title?: string }) {
  const submit = useSubmit();
  const { theme, setTheme } = useTheme();

  const { isOpen, onOpenChange, onOpen, onClose } = useDisclosure();

  const [user, setUser] = useAtom(userAtom);

  const [storedCategoryList, setCategoryList] = useAtom(categoryList);

  const navigation = useNavigation();

  const params = useParams();

  const location = useLocation();

  const data = useLoaderData<typeof loader>();

  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const setQuotesList = useSetAtom(quotesList);
  const [cart, setCartList] = useAtom(cartList);

  const handleLogout = () => {
    const form = new FormData();
    form.append("logout", "true");

    // Clear local state + storage
    setUser(null);
    setQuotesList([]);
    localStorage.removeItem(storageKey.user);
    localStorage.removeItem(storageKey.cart);
    localStorage.removeItem(storageKey.requestQuote);

    // Submit logout to backend
    submit(form, { method: "post" });

    setTimeout(() => {
      window.location.href = "/";
    }, 500);
  };

  useEffect(() => {
    if (data?.user) {
      const userStatus = data.user.status;
      if (userStatus === "suspended") {
        onOpen();
      }

      setUser(data.user as any);
    } else {
      setUser(null);
      localStorage.removeItem(storageKey.user);
    }
  }, [data?.user]);

  useEffect(() => {
    if (data && data?.categories && data?.categories.length > 0) {
      setCategoryList(data.categories);
    } else {
      fetchCategoryApi()
        .then((data) => {
          setCategoryList(data);
        })
        .catch((error) => {
          console.error("Failed to fetch categories:", error);
        });
    }
  }, [data]);
const isProductPage = location?.pathname.split("/").length > 4;
  return (
    <html suppressHydrationWarning={true} lang="en">
      <head>
        <meta
          name="theme-color"
          content={theme === "light" ? "#ffffff" : "#111827"}
          suppressHydrationWarning
        />

        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <meta name="robots" content="index, follow" />
        <meta
          name="google-site-verification"
          content="hOmxdw6kH8yFMU8QVuvW77TosBLya4PfiFkiWodPEtM"
        />
        <meta name="author" content="Promotional Products Now" />
        <meta name="og:site_name" content="Promotional Products Now" />
        <meta name="application-name" content="Promotional Products Now" />
        <meta
          name="description"
          content="Promotional Products Now is a progressive promotional products company founded on the belief that customer satisfaction is of paramount and continuing importance."
        />

        <Meta />
        {props.title ? <title>{props.title}</title> : <title>Promotional Products Now</title>}
        <Links />

        {/* Non-blocking CSS loads: start as "preload" and convert to stylesheet on load */}
        {cssBundleHref && (
          <link
            rel="preload"
            as="style"
            href={cssBundleHref}
            onLoad={(e) => {
              const linkEl = e.currentTarget as HTMLLinkElement;
              linkEl.rel = "stylesheet";
            }}
          />
        )}
        {toastifyCss && (
          <link
            rel="preload"
            as="style"
            href={toastifyCss}
            onLoad={(e) => {
              const linkEl = e.currentTarget as HTMLLinkElement;
              linkEl.rel = "stylesheet";
            }}
          />
        )}
        {swiperFreeModeCss && (
          <>
            <link
              rel="stylesheet"
              href={swiperFreeModeCss}
              media="print"
              onLoad={(e) => {
                // Make stylesheet active after it has loaded (prevents render-blocking).
                (e.currentTarget as HTMLLinkElement).media = "all";
              }}
            />
            <noscript>
              <link rel="stylesheet" href={swiperFreeModeCss} />
            </noscript>
          </>
        )}
        {swiperBundleCss && (
          <>
            <link
              rel="stylesheet"
              href={swiperBundleCss}
              media="print"
              onLoad={(e) => {
                (e.currentTarget as HTMLLinkElement).media = "all";
              }}
            />
            <noscript>
              <link rel="stylesheet" href={swiperBundleCss} />
            </noscript>
          </>
        )}
      </head>
      <body>
        <QueryClientProvider client={queryClient}>
          <CartMergeHandler />
          <SortOrderSync />
          <HeroUIProvider>
            <ThemeProvider attribute="class" defaultTheme="light">
              <ProgressBar />
              <div className="flex flex-col justify-between h-screen">
                <Header
                  sidebarOpen={isSidebarOpen}
                  setSidebarOpen={setIsSidebarOpen}
                  categories={data?.categories}
                />

                <Sidebar
                  categories={storedCategoryList}
                  sidebarOpen={isSidebarOpen}
                  setSidebarOpen={setIsSidebarOpen}
                />

                <main className="flex-1">
                  <div
                    className={`${
                      location?.pathname !== "/" && location?.pathname !== "/guarantees"
                        ? isProductPage
                          ? "container mx-auto lg:mb-6 py-3 lg:px-12"
                          : "container mx-auto lg:mb-6 py-3 lg:px-12 lg:w-11/12"
                        : "lg:mb-6 mb-3 mx-auto"
                    }`}
                  >
                    {navigation?.state === "loading" &&
                    navigation?.location?.pathname.split("/")?.length > 4 &&
                    !params.slug ? (
                      <div className="container mx-auto lg:mb-6 py-3 lg:px-12">
                        <ProductPageSkeleton />
                      </div>
                    ) : (
                      props.children
                    )}
                  </div>
                </main>
                <Footer />
              </div>
              <ToastContainer
                containerId={"ppn"}
                newestOnTop={true}
                position="top-right"
                autoClose={5000}
                hideProgressBar
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover={false}
                transition={Flip}
                theme={"colored"}
                toastClassName="dark:bg-content1 bg-white"
                bodyClassName="dark:text-foreground text-default-900"
              />

              <Modal
                size="md"
                isDismissable={false}
                isKeyboardDismissDisabled={false}
                hideCloseButton={true}
                isOpen={isOpen}
                onOpenChange={onOpenChange}
                onClose={onClose}
              >
                <ModalContent>
                  <ModalBody>
                    <div className="flex flex-col p-6 space-y-8">
                      <div className="rounded-full bg-light w-14 h-14 flex justify-center items-center mx-auto">
                        <div className="rounded-full bg-zinc-100 dark:bg-zinc-400 w-14 h-14 flex justify-center items-center aspect-square">
                          <RiUserUnfollowLine className="text-3xl text-red-500 dark:text-red-100" />
                        </div>
                      </div>
                      <div className="text-center space-y-4">
                        <h3 className="text-lg font-medium text-dark dark:text-white">
                          Your Account Has Been Suspended
                        </h3>

                        <p className=" dark:text-white">
                          Your Account has Been Suspended. Your access has been suspended due to
                          account inactivity for a prolonged period, or due to a violation of our
                          policies. If you believe this is a mistake, please contact us on{" "}
                          <Link
                            to={`tel:+1300 664999`}
                            className="font-normal text-primary"
                            aria-label="Contact us: +1300 664999"
                          >
                            +1300 664999
                          </Link>
                        </p>
                      </div>
                    </div>
                  </ModalBody>
                  <ModalFooter>
                    <Button variant="light" color="danger" onPress={() => handleLogout()}>
                      Log out
                    </Button>
                  </ModalFooter>
                </ModalContent>
              </Modal>
              <NeedsToLoginModal />
              <PreviewProduct />
            </ThemeProvider>
          </HeroUIProvider>
        </QueryClientProvider>

        {/* Place non-critical scripts at the end of the body */}
        <ScrollRestoration />
        {/* Google Analytics (GA4) + Google Ads */}
        <script async src="https://www.googletagmanager.com/gtag/js?id=G-W1TJ6B8SM7" />
        <script
          suppressHydrationWarning
          dangerouslySetInnerHTML={{
            __html: `window.dataLayer = window.dataLayer || [];
function gtag(){dataLayer.push(arguments);}
gtag('js', new Date());
gtag('config', 'G-W1TJ6B8SM7');
gtag('config', 'AW-1071697805');`,
          }}
        />
        {/* ContentSquare / Heatmap (Hotjar-style UX analytics) */}
        <script async src="https://t.contentsquare.net/uxa/3e7035065a6da.js" />

        <ChatWidget email={user?.email?.address || ""} name={user?.firstName || ""} />
        <Scripts />
      </body>
    </html>
  );
}

export function ErrorBoundary() {
  const error = useRouteError() as RouteErrorResponse | NetworkError | Error | AxiosError;

  const getFriendlyMessage = (status: number) => {
    switch (status) {
      case 404:
        return {
          title: "Page Not Found",
          message: "Sorry, the page you are looking for does not exist.",
          suggestion: "Please check the URL and try again, or go back to the homepage.",
          icon: HiExclamationCircle,
        };
      case 400:
        return {
          title: "Bad Request",
          message: "There was an error with your request. Please try again.",
          suggestion: "Please check the information you provided and try again.",
          icon: MdOutlineErrorOutline,
        };
      case 429:
        return {
          title: "Too Many Requests",
          message: "You have made too many requests in a short period.",
          suggestion: "Please wait a few minutes before trying again.",
          icon: HiExclamationCircle,
        };
      case 500:
        return {
          title: "Internal Server Error",
          message: "Oops! Something went wrong on our end.",
          suggestion: "Please try again later. If the issue persists, contact support.",
          icon: MdOutlineErrorOutline,
        };
      default:
        return {
          title: "Unexpected Error",
          message: "An unexpected error occurred.",
          suggestion: "Please try again. If the issue persists, contact support.",
          icon: MdOutlineErrorOutline,
        };
    }
  };

  const isNetworkError = (error: NetworkError): boolean =>
    error.message === "Failed to fetch" || error.name === "NetworkError";

  const isConnectionRefusedError = (error: Error): boolean =>
    error.message.includes("ECONNREFUSED");

  const isAxiosError = (error: unknown): error is AxiosError<{ message: string }> => {
    return axios.isAxiosError(error);
  };

  const handleReload = () => {
    window.location.reload();
  };

  const handleGoHome = () => {
    window.location.href = "/";
  };

  function ErrorHandler() {
    if (isAxiosError(error)) {
      const {
        title,
        message,
        suggestion,
        icon: Icon,
      } = getFriendlyMessage(error.response?.status || 500);
      return (
        <Document title={title}>
          <div className="flex flex-col justify-center items-center min-h-screen bg-white dark:bg-gray-950 px-4 py-8">
            <div className="w-full max-w-2xl text-center space-y-6">
              <div className="flex justify-center">
                <div className="rounded-full bg-red-50 dark:bg-red-950/30 p-6">
                  <Icon className="w-16 h-16 text-red-600 dark:text-red-400" />
                </div>
              </div>
              {error.response?.status && (
                <div className="space-y-2">
                  <h1 className="text-7xl md:text-9xl font-bold text-gray-300 dark:text-gray-700">
                    {error.response?.status}
                  </h1>
                </div>
              )}
              <div className="space-y-3">
                <h2 className="text-2xl md:text-3xl font-semibold text-gray-900 dark:text-gray-50">
                  {title}
                </h2>
                <p className="text-base md:text-lg text-gray-600 dark:text-gray-400">{message}</p>
                <p className="text-sm md:text-base text-gray-500 dark:text-gray-500">
                  {suggestion}
                </p>
              </div>
              {error.response?.data && (
                <div className="mt-6 p-4 bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-800">
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {error.response.data?.message || JSON.stringify(error.response?.data)}
                  </p>
                </div>
              )}
              <div className="flex flex-col sm:flex-row gap-3 justify-center items-center pt-6">
                <Button
                  color="primary"
                  variant="solid"
                  onPress={handleGoHome}
                  startContent={<HiOutlineHome className="w-5 h-5" />}
                  className="w-full sm:w-auto"
                >
                  Go Home
                </Button>
                <Button
                  color="default"
                  variant="bordered"
                  onPress={handleReload}
                  startContent={<HiOutlineRefresh className="w-5 h-5" />}
                  className="w-full sm:w-auto"
                >
                  Reload Page
                </Button>
              </div>
            </div>
          </div>
        </Document>
      );
    } else if (isRouteErrorResponse(error)) {
      const { title, message, suggestion, icon: Icon } = getFriendlyMessage(error?.status);
      return (
        <Document title={title}>
          <div className="flex flex-col justify-center items-center min-h-screen bg-white dark:bg-gray-950 px-4 py-8">
            <div className="w-full max-w-2xl text-center space-y-6">
              <div className="flex justify-center">
                <div className="rounded-full bg-red-50 dark:bg-red-950/30 p-6">
                  <Icon className="w-16 h-16 text-red-600 dark:text-red-400" />
                </div>
              </div>
              {error?.status && (
                <div className="space-y-2">
                  <h1 className="text-7xl md:text-9xl font-bold text-gray-300 dark:text-gray-700">
                    {error?.status}
                  </h1>
                </div>
              )}
              <div className="space-y-3">
                <h2 className="text-2xl md:text-3xl font-semibold text-gray-900 dark:text-gray-50">
                  {title}
                </h2>
                <p className="text-base md:text-lg text-gray-600 dark:text-gray-400">{message}</p>
                <p className="text-sm md:text-base text-gray-500 dark:text-gray-500">
                  {suggestion}
                </p>
              </div>
              {error?.data && (
                <div className="mt-6 p-4 bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-800">
                  <p className="text-sm text-gray-600 dark:text-gray-400">{error?.data}</p>
                </div>
              )}
              <div className="flex flex-col sm:flex-row gap-3 justify-center items-center pt-6">
                <Button
                  color="primary"
                  variant="solid"
                  onPress={handleGoHome}
                  startContent={<HiOutlineHome className="w-5 h-5" />}
                  className="w-full sm:w-auto"
                >
                  Go Home
                </Button>
                <Button
                  color="default"
                  variant="bordered"
                  onPress={handleReload}
                  startContent={<HiOutlineRefresh className="w-5 h-5" />}
                  className="w-full sm:w-auto"
                >
                  Reload Page
                </Button>
              </div>
            </div>
          </div>
        </Document>
      );
    } else if (isNetworkError(error as NetworkError)) {
      return (
        <Document title="Network Error">
          <div className="flex flex-col justify-center items-center min-h-screen bg-white dark:bg-gray-950 px-4 py-8">
            <div className="w-full max-w-2xl text-center space-y-6">
              <div className="flex justify-center">
                <div className="rounded-full bg-orange-50 dark:bg-orange-950/30 p-6">
                  <MdOutlineWifiOff className="w-16 h-16 text-orange-600 dark:text-orange-400" />
                </div>
              </div>
              <div className="space-y-3">
                <h2 className="text-2xl md:text-3xl font-semibold text-gray-900 dark:text-gray-50">
                  Network Error
                </h2>
                <p className="text-base md:text-lg text-gray-600 dark:text-gray-400">
                  It seems like you&apos;re having network issues.
                </p>
                <p className="text-sm md:text-base text-gray-500 dark:text-gray-500">
                  Please check your internet connection and try again.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 justify-center items-center pt-6">
                <Button
                  color="primary"
                  variant="solid"
                  onPress={handleGoHome}
                  startContent={<HiOutlineHome className="w-5 h-5" />}
                  className="w-full sm:w-auto"
                >
                  Go Home
                </Button>
                <Button
                  color="default"
                  variant="bordered"
                  onPress={handleReload}
                  startContent={<HiOutlineRefresh className="w-5 h-5" />}
                  className="w-full sm:w-auto"
                >
                  Try Again
                </Button>
              </div>
            </div>
          </div>
        </Document>
      );
    } else if (isConnectionRefusedError(error)) {
      return (
        <Document title="Connection Error">
          <div className="flex flex-col justify-center items-center min-h-screen bg-white dark:bg-gray-950 px-4 py-8">
            <div className="w-full max-w-2xl text-center space-y-6">
              <div className="flex justify-center">
                <div className="rounded-full bg-red-50 dark:bg-red-950/30 p-6">
                  <MdOutlineWifiOff className="w-16 h-16 text-red-600 dark:text-red-400" />
                </div>
              </div>
              <div className="space-y-3">
                <h2 className="text-2xl md:text-3xl font-semibold text-gray-900 dark:text-gray-50">
                  Connection Error
                </h2>
                <p className="text-base md:text-lg text-gray-600 dark:text-gray-400">
                  We couldn&apos;t connect to the server.
                </p>
                <p className="text-sm md:text-base text-gray-500 dark:text-gray-500">
                  Please make sure the server is running and try again.
                </p>
              </div>
              {error.message && (
                <div className="mt-6 p-4 bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-800">
                  <p className="text-sm text-gray-600 dark:text-gray-400">{error.message}</p>
                </div>
              )}
              <div className="flex flex-col sm:flex-row gap-3 justify-center items-center pt-6">
                <Button
                  color="primary"
                  variant="solid"
                  onPress={handleGoHome}
                  startContent={<HiOutlineHome className="w-5 h-5" />}
                  className="w-full sm:w-auto"
                >
                  Go Home
                </Button>
                <Button
                  color="default"
                  variant="bordered"
                  onPress={handleReload}
                  startContent={<HiOutlineRefresh className="w-5 h-5" />}
                  className="w-full sm:w-auto"
                >
                  Try Again
                </Button>
              </div>
            </div>
          </div>
        </Document>
      );
    } else if (error instanceof Error) {
      return (
        <Document title={error.message}>
          <div className="flex flex-col justify-center items-center min-h-screen bg-white dark:bg-gray-950 px-4 py-8">
            <div className="w-full max-w-2xl text-center space-y-6">
              <div className="flex justify-center">
                <div className="rounded-full bg-red-50 dark:bg-red-950/30 p-6">
                  <MdOutlineErrorOutline className="w-16 h-16 text-red-600 dark:text-red-400" />
                </div>
              </div>
              <div className="space-y-3">
                <h2 className="text-2xl md:text-3xl font-semibold text-gray-900 dark:text-gray-50">
                  Oops, Something went wrong
                </h2>
                <p className="text-base md:text-lg text-gray-600 dark:text-gray-400">
                  But don&apos;t worry - it&apos;s not your fault.
                </p>
                <p className="text-sm md:text-base text-gray-500 dark:text-gray-500">
                  Please try reloading the page or contact support if the issue persists.
                </p>
              </div>
              {error.message &&
                (isNetworkError(error as NetworkError) || isConnectionRefusedError(error)) && (
                  <div className="mt-6 p-4 bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-800 text-left">
                    <p className="text-xs font-medium text-gray-500 dark:text-gray-500 mb-2">
                      Error Details:
                    </p>
                    <pre className="text-xs text-gray-700 dark:text-gray-300 whitespace-pre-wrap break-words">
                      {error.message}
                    </pre>
                  </div>
                )}
              <div className="flex flex-col sm:flex-row gap-3 justify-center items-center pt-6">
                <Button
                  color="primary"
                  variant="solid"
                  onPress={handleGoHome}
                  startContent={<HiOutlineHome className="w-5 h-5" />}
                  className="w-full sm:w-auto"
                >
                  Go Home
                </Button>
                <Button
                  color="default"
                  variant="bordered"
                  onPress={handleReload}
                  startContent={<HiOutlineRefresh className="w-5 h-5" />}
                  className="w-full sm:w-auto"
                >
                  Reload Page
                </Button>
              </div>
            </div>
          </div>
        </Document>
      );
    } else {
      return (
        <Document title="Unknown Error">
          <div className="flex flex-col justify-center items-center min-h-screen bg-white dark:bg-gray-950 px-4 py-8">
            <div className="w-full max-w-2xl text-center space-y-6">
              <div className="flex justify-center">
                <div className="rounded-full bg-gray-50 dark:bg-gray-900 p-6">
                  <HiExclamationCircle className="w-16 h-16 text-gray-600 dark:text-gray-400" />
                </div>
              </div>
              <div className="space-y-3">
                <h2 className="text-2xl md:text-3xl font-semibold text-gray-900 dark:text-gray-50">
                  Unknown Error
                </h2>
                <p className="text-base md:text-lg text-gray-600 dark:text-gray-400">
                  An unexpected error occurred.
                </p>
                <p className="text-sm md:text-base text-gray-500 dark:text-gray-500">
                  Please try again later or contact support if the issue persists.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 justify-center items-center pt-6">
                <Button
                  color="primary"
                  variant="solid"
                  onPress={handleGoHome}
                  startContent={<HiOutlineHome className="w-5 h-5" />}
                  className="w-full sm:w-auto"
                >
                  Go Home
                </Button>
                <Button
                  color="default"
                  variant="bordered"
                  onPress={handleReload}
                  startContent={<HiOutlineRefresh className="w-5 h-5" />}
                  className="w-full sm:w-auto"
                >
                  Try Again
                </Button>
              </div>
            </div>
          </div>
        </Document>
      );
    }
  }

  return <ErrorHandler />;
}
